"use client";
import React, { useCallback, useState, useEffect } from "react";
import CitiesCombobox from "./CitiesCombobox";
import { useCities } from "@/lib/cities.data";
import { Button, buttonVariants } from "./ui/button";
import { ChevronRight, SearchIcon } from "lucide-react";
import { Separator } from "./ui/separator";
import { cn, removeFalsyValues } from "@/lib/utils";
import Counter from "./counter";
import useSizes from "@/lib/window-sizes-hook";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTitle,
  DrawerTrigger,
} from "./ui/drawer";
import RangePicker from "./RangePicker";
import { useLocale, useTranslations } from "next-intl";
import Filters from "@/src/app/all-units/components/Filters";
import { SearchFilterIcon } from "./icons";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { setSearchParams } from "@/redux/slices/searchParams.slice";
import Link from "next/link";
import { usePathname } from "next/navigation";

const Search = ({
  className,
  ismap,
  noSearchBtn,
}: {
  className?: string;
  ismap?: boolean;
  noSearchBtn?: boolean;
}) => {
  const { data: cities, isLoading } = useCities();
  const t = useTranslations();
  const [open, setOpen] = useState(false);
  const locale = useLocale();
  const path = usePathname();
  const isClient = useSelector((state: RootState) => state.isBrowser);
  const query = useSelector((state: RootState) => state.searchParams);
  const { maxSm } = useSizes();
  const dispatch = useDispatch();

  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, [isClient]);

  const setCounter = useCallback(
    (count: number) => dispatch(setSearchParams({ numOfGuests: count })),
    [dispatch]
  );

  if (!isMounted) {
    return null; // Avoid rendering on the server
  }

  if (maxSm && isClient && path !== "/") {
    return (
      <Drawer
        {...{ open, onOpenChange: setOpen }}
        dismissible={false}
        direction={locale === "ar" ? "left" : "right"}
      >
        <DrawerTrigger asChild>
          <Button
            variant={"outline"}
            aria-label="search type trigger"
            className={cn(
              "text-gray-light w-full mt-4 flex-1 justify-start gap-3",
              { "w-fit flex-none justify-end": ismap }
            )}
          >
            {ismap ? (
              <>
                <SearchFilterIcon />
              </>
            ) : (
              <>
                <SearchIcon />
                {t("search.startSearch")}
              </>
            )}
          </Button>
        </DrawerTrigger>
        <DrawerContent className="bg-white  w-full flex-col !touch-auto">
          <DrawerTitle />
          <div className="relative overflow-y-auto h-full w-full">
            <div className="bg-white py-2 mb-6 z-10 sticky top-0">
              <DrawerClose asChild onClick={setOpen.bind(null, false)}>
                <Button size={"icon"}>
                  <ChevronRight className="ltr:rotate-180" />
                </Button>
              </DrawerClose>
            </div>
            <div className="w-full space-y-4 flex-1">
              <div className="flex-1">
                <CitiesCombobox
                  list={
                    cities?.map((city) => ({
                      value: city.id,
                      label: locale === "ar" ? city.arabicName : city.name,
                    })) || []
                  }
                  isLoading={isLoading}
                  placeholder={t("search.city")}
                  searchPlaceholder={t("search.selectCity")}
                />
              </div>
              <div className="flex-1">
                <RangePicker />
              </div>
              <div
                className={cn(
                  buttonVariants({ variant: "outline" }),
                  "w-full justify-between"
                )}
              >
                <span>{t("search.numOfGuests")} </span>
                <Counter
                  countNumber={query?.numOfGuests}
                  setCountNumber={setCounter}
                  min={1}
                />
              </div>
            </div>
            <Filters />
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <div
      className={cn(
        "bg-green-background mt-10 p-2 sm:px-4 sm:py-3 rounded-2xl max-w-5xl w-full mx-auto",
        className
      )}
    >
      <div className="max-sm:space-y-1 sm:flex bg-white shadow-md rounded-2xl p-3 mb-2 sm:mb-5">
        <div className="flex-1">
          <CitiesCombobox
            list={
              cities?.map((city) => ({
                value: city.id,
                label: locale === "ar" ? city.arabicName : city.name,
              })) || []
            }
            isLoading={isLoading}
            placeholder={t("search.city")}
            searchPlaceholder={t("search.selectCity")}
          />
        </div>
        <Separator orientation="vertical" />
        <div className="flex-1">
          <RangePicker />
        </div>
        <Separator orientation="vertical" />
        <div
          className={cn(
            buttonVariants({ variant: "outline" }),
            "flex-1 flex justify-between items-center"
          )}
        >
          <span className="text-[12px] text-gray-dark">
            {t("search.numOfGuests")}{" "}
          </span>
          <Counter
            min={1}
            countNumber={query?.numOfGuests || 1}
            setCountNumber={setCounter}
          />
        </div>
      </div>
      {!noSearchBtn && (
        <div className="flex justify-end">
          <Button className="h-10 px-9 self-end" aria-label="search" asChild>
            <Link
              href={{
                pathname: "/all-units",
                query: {
                  ...removeFalsyValues(query),
                  page: 1,
                },
              }}
              prefetch
              aria-label="search"
            >
              {t("albhth")}
            </Link>
          </Button>
        </div>
      )}
    </div>
  );
};

export default Search;
